/* eslint-disable react/no-unstable-nested-components */
import { createColumnHelper, getCoreRowModel, getPaginationRowModel, useReactTable } from "@tanstack/react-table";
import DividendsTable from "components/company/v3/contents/events/dividends/DividendsTable";
import classNames from "components/ui/classNames";
import differenceInYears from "date-fns/differenceInYears";
import getYear from "date-fns/getYear";
import isValid from "date-fns/isValid";
import formatDateOrReturnHyphen from "lib/utils/formatDateOrReturnHyphen";
import parseDateYmd from "lib/utils/parseDateYmd";
import trimEnd from "lodash/trimEnd";
import numeral from "numeral";
import type { IntlShape } from "react-intl";
import { FormattedMessage, useIntl } from "react-intl";
import type { IDividendsPaidDetails } from "types/graphqlTypes";

interface DividendsPaidProps {
  dividendsPaid: IDividendsPaidDetails[];
  currency: "BRL" | "USD";
  hasPayDate: boolean;
  isRenderPageDefault?: boolean;
}

export default function DividendsPaid({
  dividendsPaid,
  currency,
  hasPayDate,
  isRenderPageDefault = false,
}: DividendsPaidProps) {
  const columnHelper = createColumnHelper<IDividendsPaidDetails>();
  const intl = useIntl();

  const formatAmount = (amount: number) => {
    if (currency === "BRL") {
      return numeral(amount).format("0.00").replace(".", ",");
    }
    return trimEnd(trimEnd(numeral(amount).format("0.000").replace(".", ","), "0"), ",");
  };

  // Brazilian-specific columns
  const dividendTypeColumn = columnHelper.accessor("dividendType", {
    header: () => <FormattedMessage defaultMessage="Tipo" id="wt+XcA" />,
    cell: (info) => (
      <div
        className={classNames(
          "whitespace-nowrap",
          "px-4 py-1.5 text-sm font-normal",
          "text-slate-600 dark:text-slate-300",
        )}
      >
        <span
          className={classNames(
            "whitespace-nowrap rounded-md px-2 py-1 text-xs font-bold tracking-wide text-slate-600 dark:text-slate-300",
            {
              "bg-sky-600/10 text-sky-800 dark:bg-sky-800/10 dark:text-sky-300": info.getValue() === "JUROS",
              "bg-yellow-600/10 text-yellow-800 dark:bg-yellow-800/10 dark:text-yellow-300":
                info.getValue() === "DIVIDENDO",
              "bg-purple-600/10 text-purple-800 dark:bg-violet-800/10 dark:text-violet-300":
                info.getValue() === "RENDIMENTO",
            },
          )}
        >
          {info.getValue() === "JUROS" && "JCP"}
          {info.getValue() === "DIVIDENDO" && intl.formatMessage({ defaultMessage: "Dividendo", id: "/E+MJo" })}
          {info.getValue() === "RENDIMENTO" && intl.formatMessage({ defaultMessage: "Rendimento", id: "Egt9i5" })}
        </span>
      </div>
    ),
  });

  const recordDateColumn = columnHelper.accessor("recordDate", {
    header: () => <FormattedMessage defaultMessage="Data-Com" id="uJFyej" />,
    cell: (info) => (
      <div
        className={classNames(
          "whitespace-nowrap",
          "px-4 py-1.5 text-sm font-normal",
          "text-slate-600 dark:text-slate-300",
        )}
      >
        <span>
          {intl.formatDate(parseDateYmd(info.getValue()), {
            year: "numeric",
            month: "numeric",
            day: "numeric",
          })}
        </span>
      </div>
    ),
  });

  // Shared columns
  const excludingDateColumn = columnHelper.accessor("excludingDate", {
    header: () => <FormattedMessage defaultMessage="Data-Ex" id="tsg1zP" />,
    cell: (info) => (
      <div
        className={classNames(
          "whitespace-nowrap",
          "px-4 py-1.5 text-sm font-normal",
          "text-slate-600 dark:text-slate-300",
        )}
      >
        <span>
          {currency === "BRL"
            ? getExcludingDate(intl, parseDateYmd(info.row.original.recordDate), parseDateYmd(info.getValue()))
            : formatDateOrReturnHyphen(intl, info.getValue())}
        </span>
      </div>
    ),
    meta:
      currency === "USD"
        ? {
            header: {
              className: "w-1/3",
            },
            cell: { className: "w-1/3" },
          }
        : undefined,
  });

  const payDateColumn = columnHelper.accessor("payDate", {
    header: () => <FormattedMessage defaultMessage="Pagamento" id="23+rmI" />,
    cell: (info) => (
      <div
        className={classNames(
          "whitespace-nowrap",
          "px-4 py-1.5 text-sm font-normal",
          "text-slate-600 dark:text-slate-300",
        )}
      >
        <span>
          {(() => {
            if (currency === "BRL") {
              const value = info.getValue();
              if (value) {
                return getPayDate(parseDateYmd(value), intl);
              }
              return "-";
            }
            return formatDateOrReturnHyphen(intl, info.getValue());
          })()}
        </span>
      </div>
    ),
  });

  const amountPaidColumn = columnHelper.accessor("amountPaid", {
    header: () =>
      currency === "BRL" ? (
        <FormattedMessage defaultMessage="Valor (R$)" description="Valor dos dividendos pagos em reais" id="XydT1i" />
      ) : (
        <FormattedMessage defaultMessage="Valor (U$)" description="Valor dos dividendos pagos em dólar" id="gHZX1a" />
      ),
    cell: (info) => (
      <div
        className={classNames(
          "whitespace-nowrap",
          "px-4 py-1.5 text-sm font-normal",
          "text-slate-600 dark:text-slate-300",
        )}
      >
        <span>{formatAmount(info.getValue())}</span>
      </div>
    ),
    meta:
      currency === "USD"
        ? {
            header: { className: "w-1/3" },
            cell: { className: "w-1/3" },
          }
        : undefined,
  });

  // American-specific column
  const distributionFrequencyColumn = columnHelper.accessor("distributionFrequency", {
    header: () => <FormattedMessage defaultMessage="Frequência" id="0w0ol9" />,
    cell: (info) => (
      <div
        className={classNames(
          "whitespace-nowrap px-4 py-1.5 text-sm font-normal text-slate-600",
          "dark:text-slate-300",
        )}
      >
        <span>{getLabelForDividendFrequency(info.getValue())}</span>
      </div>
    ),
    meta: {
      header: { className: "w-1/3" },
      cell: { className: "w-1/3" },
    },
  });

  // Build columns based on currency
  const columns =
    currency === "BRL"
      ? [
          dividendTypeColumn,
          recordDateColumn,
          excludingDateColumn,
          ...(hasPayDate ? [payDateColumn] : []),
          amountPaidColumn,
        ]
      : [excludingDateColumn, amountPaidColumn, distributionFrequencyColumn];

  const table = useReactTable({
    data: dividendsPaid,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: isRenderPageDefault ? 8 : 16,
      },
    },
  });

  return <DividendsTable table={table} />;
}

export function getExcludingDate(intl: IntlShape, recordDate: Date, excludingDate: Date) {
  const difference = differenceInYears(excludingDate, recordDate);

  if (difference >= 2) {
    return "-";
  }

  return intl.formatDate(excludingDate, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

export function getPayDate(payDate: Date, intl: IntlShape) {
  if (getYear(payDate) === 1900 || !isValid(payDate)) {
    return "-";
  }

  if (getYear(payDate) >= 2500) {
    return intl.formatMessage({
      defaultMessage: "Sem data definida",
      id: "vf+lDR",
      description: "Mensagem exibida quando a data de pagamento de dividendos não está definida",
    });
  }

  return intl.formatDate(payDate, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

function getLabelForDividendFrequency(value?: number | null) {
  switch (value) {
    case 1:
      return (
        <FormattedMessage
          defaultMessage="Anual"
          description="Periodicidade para distribuição dos dividendos"
          id="v0bpXX"
        />
      );
    case 2:
      return (
        <FormattedMessage
          defaultMessage="Semestral"
          description="Periodicidade para distribuição dos dividendos"
          id="521Yp1"
        />
      );
    case 4:
      return (
        <FormattedMessage
          defaultMessage="Trimestral"
          description="Periodicidade para distribuição dos dividendos"
          id="pKAHhM"
        />
      );
    case 12:
      return (
        <FormattedMessage
          defaultMessage="Mensal"
          description="Periodicidade para distribuição dos dividendos"
          id="Up/Fj3"
        />
      );

    default:
      return "-";
  }
}
